import { useAuthProfile } from "../../app/hooks";
import React, { useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    CircularProgress,
    Box,
} from "@mui/material";
import {  selectStateRTK } from "../../features/userTokenManagementSlice";
import { useSelector } from "react-redux";
import { printError } from "../../app/utils";
import { traceLog, clearTrace } from "../../app/debugTrace";
import { INITIAL_URL_SEARCH } from "../..";

type AuthProfileDialogProps = {
    open?: boolean;
    onClose?: () => void;
};

// Recursive renderer for user object using Typography with indentation
const renderObject = (obj: any, depth = 1): React.ReactNode[] => {
    if (obj === null || obj === undefined || typeof obj !== "object") {
        return [
            <Typography
                key={Math.random()}
                sx={{ pl: depth * 2 }}
                variant="body2"
            >
                {String(obj)}
            </Typography>,
        ];
    }

    return Object.entries(obj).flatMap(([key, value]) => {
        const padding = depth * 2; // every level indented, including first

        if (typeof value === "function") {
            // show function name with ()
            return (
                <Typography key={key} sx={{ pl: padding }} variant="body2">
                    <Typography component="span" sx={{ fontWeight: 'bold', fontStyle: 'italic' }}>
                        {key}
                    </Typography>
                    : {value.name || "function"}()
                </Typography>
            );
        }

        if (typeof value === "object" && value !== null) {
            return [
                <Typography key={key} sx={{ pl: padding }} variant="body2">
                    <Typography component="span" sx={{ fontWeight: 'bold', fontStyle: 'italic' }}>
                        {key}
                    </Typography>
                    :
                </Typography>,
                ...renderObject(value, depth + 1), // recursively indent nested objects
            ];
        }

        // primitive values
        return (
            <Typography key={key} sx={{ pl: padding }} variant="body2">
                <Typography component="span" sx={{ fontWeight: 'bold', fontStyle: 'italic' }}>
                    {key}
                </Typography>
                : {String(value)}
            </Typography>
        );
    });
};

export function AuthProfileDialog({ open: openProp, onClose }: AuthProfileDialogProps) {
    const [open, setOpen] = useState(true);
    const sessionRTK = useSelector(selectStateRTK)
    const handleOpen = () => setOpen(true);
    const handleClose = () => {
        setOpen(false);
        onClose?.();
    };

    const {
        profile,
        isLoading: isAuthProfileLoading,
        isProfileSuccess,
        authUser: user,
        error: errorAuthenticator,
        isAuthenticated,
        isLoading: isLoadingAuth,
        isSuccess: isAuthSuccess,
        authMethod,
        customAuth,
    } = useAuthProfile();

    return (
        <>
            {/* <Button variant="outlined" onClick={handleOpen}>
        Show Auth Profile
      </Button> */}

            <Dialog open={openProp ?? open} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle>Auth Profile Data</DialogTitle>
                <DialogContent dividers>
                    {/* {(isAuthProfileLoading || isLoadingAuth) && (
                        <Box sx={{ textAlign: "center", p: 2 }}>
                            <CircularProgress />
                            <Typography>Loading...</Typography>
                        </Box>
                    )} */}

                    {!isAuthProfileLoading && !isLoadingAuth && (
                        <>
                            <Typography variant="subtitle1">errorAuthenticator: {printError(errorAuthenticator)}</Typography>
                            <Typography variant="subtitle1">isProfileSuccess: {String(isProfileSuccess)}</Typography>
                            <Typography variant="subtitle1">isAuthSuccess: {String(isAuthSuccess)}</Typography>
                            <Typography variant="subtitle1">isAuthenticated: {String(isAuthenticated)}</Typography>
                            <Typography variant="subtitle1">authMethod: {String(authMethod)}</Typography>
                            <Typography variant="subtitle1">customAuth: {profile ? renderObject(customAuth) : <Typography>No customAuth data</Typography>}</Typography>


                            <Typography variant="h6">profile:</Typography>
                            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                                {/* {JSON.stringify(profile, null, 2)} */}
                                {profile ? renderObject(profile) : <Typography>No profile data</Typography>}
                            </Typography>


                            <Typography variant="h6" sx={{ mt: 2 }}>
                                User Object:
                            </Typography>
                            {user ? renderObject(user) : <Typography>No user data</Typography>}

                            <Typography variant="h6" sx={{ mt: 2 }}>
                                RTK State:
                            </Typography>
                            {sessionRTK ? renderObject(sessionRTK) : <Typography>No RTK State data</Typography>}

                            <Typography variant="h6" sx={{ mt: 2 }}>
                                Debug Trace:
                            </Typography>
                            <Typography variant="body2">INITIAL URL: {String(INITIAL_URL_SEARCH)}</Typography>
                            <Typography variant="body2">current URL: {window.location.search || '(empty)'}</Typography>
                            <Box sx={{ display: 'flex', gap: 1, my: 1 }}>
                                <Button size="small" variant="outlined" onClick={() => {
                                    const lines = [
                                        `INITIAL URL: ${INITIAL_URL_SEARCH}`,
                                        `current URL: ${window.location.search || '(empty)'}`,
                                        ...traceLog.map(e => `${e.ts.toFixed(0)}ms · ${e.tag} · ${e.detail}`),
                                    ];
                                    const text = lines.join('\n');
                                    if (navigator.clipboard && navigator.clipboard.writeText) {
                                        navigator.clipboard.writeText(text).catch(err => console.warn('clipboard.writeText failed:', err));
                                    } else {
                                        // iOS Safari fallback
                                        const ta = document.createElement('textarea');
                                        ta.value = text;
                                        ta.style.position = 'fixed';
                                        ta.style.opacity = '0';
                                        document.body.appendChild(ta);
                                        ta.select();
                                        try { document.execCommand('copy'); } catch { }
                                        document.body.removeChild(ta);
                                    }
                                }}>Copy trace</Button>
                                <Button size="small" onClick={() => { clearTrace(); setOpen(false); setTimeout(() => setOpen(true), 0); }}>Clear trace</Button>
                            </Box>
                            <Box sx={{ fontFamily: 'monospace', fontSize: '12px', whiteSpace: 'pre-wrap', wordBreak: 'break-all', maxHeight: '40vh', overflow: 'auto', bgcolor: '#f5f5f5', p: 1, borderRadius: 1 }}>
                                {traceLog.length === 0 ? '(empty)' : traceLog.map((e, i) => (
                                    <div key={i}>{e.ts.toFixed(0)}ms · {e.tag} · {e.detail}</div>
                                ))}
                            </Box>
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} color="primary">
                        Close
                    </Button>
                </DialogActions>
                <Typography variant="caption" sx={{ color: '#bbbbbb', textAlign: 'center', px: 2, pb: 1 }}>
                    Version: {process.env.REACT_APP_VERSION} {process.env.REACT_APP_BUILD_TIME ? new Date(process.env.REACT_APP_BUILD_TIME).toLocaleString(undefined, { dateStyle: 'long', timeStyle: 'short', }) : 'Unknown'}
                </Typography>
            </Dialog>
        </>
    );
}